import { Component, OnInit, Input, ViewChild, ElementRef, AfterViewInit, OnChanges } from '@angular/core';
import Chart, { ChartConfiguration, ChartData, ChartDataSets, ChartOptions } from 'chart.js';

import { ReportItem } from 'src/app/shared/models/classes/report/report-item';
import { UiService } from 'src/app/shared/services/ui.service';
import { FormatService } from 'src/app/shared/services/format.service';
import { ChartType } from 'src/app/shared/models/enums/chart-type.enum';

@Component({
  selector: 'app-citizen-report-chart',
  templateUrl: './citizen-report-chart.component.html',
  styleUrls: ['./citizen-report-chart.component.scss']
})
export class CitizenReportChartComponent implements AfterViewInit, OnChanges {
    @Input() reportItem: ReportItem;
    @ViewChild('canvas') canvas: ElementRef;

    colors: string[];

    chart: Chart;

    stacksLabels: string[];
    hasMultiStack: boolean;
    hasMultiDatasets: boolean;
    labelsArray: string[];

    constructor(
        private uiSrv: UiService,
        private formatSrv: FormatService
    ) { }

    ngAfterViewInit() {
        this.setChart();
    }

    ngOnChanges() {
        this.setChart();
    }

    private setChart(): void {
        this.colors = this.uiSrv.getChartsColors();

        if(!this.chart) {
            this.chart = new Chart(this.canvas.nativeElement, this.getChartConfig());
        } else {
            this.chart.data = this.dataFromReportItem();
            this.chart.update(0);
        }
    }

    private getChartConfig(): ChartConfiguration {
        const data = this.dataFromReportItem();
        this.labelsArray = null;

        switch(this.reportItem.chartType) {
            case ChartType.doughnut:
                this.labelsArray = this.generateLabels(data);
                return {
                    type: 'doughnut',
                    data: data,
                    options: this.setFlatChartOptions()
                };
            case ChartType.bar:
                return {
                    type: 'bar',
                    data: data,
                    options: this.setAxesChartOptions()
                };
            case ChartType.line:
                return {
                    type: 'line',
                    data: data,
                    options: this.setAxesChartOptions(false)
                };
        }
    }

    private dataFromReportItem(): ChartData {
        const labels: string[] = this.reportItem.labels;
        this.hasMultiStack = this.reportItem.stacks.length > 1;
        this.stacksLabels = [];

        const datasets: ChartDataSets[] = this.reportItem.stacks.reduce((accumulator, stack, stackIndex) => {
            this.hasMultiDatasets = stack.datasets.length > 1;

            if(this.hasMultiStack && this.hasMultiDatasets) {
                this.stacksLabels.push(stack.name);
            }

            const dataset = stack.datasets.map((d, i) => {
                let colorIndex = i;
                let bgColor: string | string[];
                let borderColor = '#FFFFFF';
                let borderWidth = 1;

                // use stack index when there are only one dataset
                if(!this.hasMultiDatasets) {
                    colorIndex = stackIndex;
                }

                switch(this.reportItem.chartType) {
                    case ChartType.doughnut:
                        bgColor = this.colors;
                        break;
                    case ChartType.bar:
                        bgColor = this.colors[colorIndex];
                        break;
                    case ChartType.line:
                        borderColor = this.colors[colorIndex];
                        bgColor = 'transparent';
                        borderWidth = 2;
                        break;
                }

                return {
                    label: d.label,
                    data: d.data,
                    stack: stack.name,
                    borderColor: borderColor,
                    backgroundColor: bgColor,
                    borderWidth: borderWidth,
                };
            });
            return [ ...accumulator, ...dataset];
        }, []);

        return {
            labels: labels,
            datasets: datasets
        };
    }

    private setFlatChartOptions(): ChartOptions {
        return {
            tooltips: {
                callbacks: {
                    label: (tooltipItem, data) => {
                        const dataset: ChartDataSets = data.datasets[tooltipItem.datasetIndex];
                        const dataValues: number[] = <number[]>dataset.data;
                        const value: number = dataValues[tooltipItem.index];
                        const total: number = dataValues.reduce((tot, val) => tot + val, 0);

                        return `${data.labels[tooltipItem.index]}: ${this.formatSrv.formatPercent(value / total)} %`;
                    }
                }
            },
            legend: {
                display: false
            }
        };
    }

    private setAxesChartOptions(doesBeginAtZero = true): ChartOptions {
        return {
            tooltips: {
                callbacks: {
                    label: (tooltipItem, data) => {
                        const dataset: ChartDataSets = data.datasets[tooltipItem.datasetIndex];
                        const value: number = <number>dataset.data[tooltipItem.index];

                        return this.formatSrv.formatAmount(value, 'fixed', 0.001) + "K€";
                    }
                }
            },
            legend: {
                display: false
            },
            scales: {
                yAxes: [{
                    ticks: {
                        beginAtZero: doesBeginAtZero,
                        callback: (value, index, values) => {
                            return this.formatSrv.formatAmount(<number>value, 'fixed') + "K€";
                        }
                    },
                    scaleLabel: {
                        display: true,
                        labelString: this.uiSrv.translate('reports.barChart.amounts.shortLabel')
                        // labelString: this.uiSrv.translate('reports.barChart.years.shortLabel')
                    },
                    stacked: true
                }],
                xAxes: [{
                    scaleLabel: {
                        display: true,
                        labelString: this.uiSrv.translate('reports.barChart.years.shortLabel')
                        // labelString: this.uiSrv.translate('reports.barChart.amounts.shortLabel')
                    },
                    stacked: true
                }]
            }
        };
    }

    private generateLabels(data: ChartData): string[] {
        const dataset = data.datasets[0];
        return data.labels.map((label, i) => {
            const value: number = <number>dataset.data[i];
            return `${label}: ${this.formatSrv.formatAmount(value, 'fixed')}K€`;
        });
    }
}
